/* In-memory mocked client for News & Communications — no real backend exists for this domain
 * yet (the /api/news route in Ocelot exists but the downstream isn't implemented — 500s in
 * practice). Mock data ported from the deleted newsHandlers.ts MSW handler. */

import { INewsClient } from './INewsClient';
import {
  NewsItemDto,
  NewsCommentDto,
  NewsTagDto,
  NewsEngagementSummaryDto,
  CreateUpdateNewsItemDto,
  CreateUpdateNewsTagDto,
  NewsItemStatus,
  getNewsItemStatus,
} from './newsTypes';

/* ---- Date helpers (keeps mock data evergreen) ---- */

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString();
}

function daysFromNow(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString();
}

/* ---- Mock Data ---- */

let mockNewsItems: NewsItemDto[] = [
  {
    id: 1,
    title: 'New Grab & Go Range - Fresh Food to Go',
    content: `<p>We're thrilled to launch our enhanced Grab & Go range, designed to capture busy customers looking for quick, quality food options.</p>
<p><strong>New Products Include:</strong></p>
<ul>
<li>Premium Sandwiches & Wraps - ready-made fresh daily</li>
<li>Hot Food Counter - sausage rolls, pasties, and pizza slices</li>
<li>Fresh Fruit Pots & Healthy Snacks</li>
<li>Chilled Coffee & Energy Drinks</li>
</ul>
<p><strong>Merchandising Tips:</strong></p>
<ul>
<li>Place hot food counter near the till for impulse purchases</li>
<li>Ensure grab & go fridges are restocked every 2 hours during peak times</li>
<li>Display meal deals prominently (sandwich + drink + snack for £3.50)</li>
</ul>
<p>Training materials and planograms are available in the Documents section.</p>`,
    summary: 'Launch of new Grab & Go food-to-go range targeting busy commuters and lunchtime trade',
    imageUrl: 'https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?w=800',
    franchiseId: 1,
    storeNumbers: null,
    isGlobal: true,
    isPinned: true,
    publishedAtUtc: daysAgo(9),
    expiresAtUtc: daysFromNow(90),
    createdAtUtc: daysAgo(14),
    createdByUserId: 1,
    createdByUserName: 'Sarah Johnson',
    updatedAtUtc: daysAgo(10),
    updatedByUserId: 1,
    updatedByUserName: 'Sarah Johnson',
    viewCount: 342,
    likeCount: 89,
    commentCount: 23,
    tags: [2, 12, 3],
  },
  {
    id: 2,
    title: 'Age Verification Compliance - Challenge 25 Reminder',
    content: '<p>Following recent test purchases, we\'re reinforcing our Challenge 25 policy across all stores.</p>',
    summary: 'Critical reminder on age verification and Challenge 25 compliance for all restricted products',
    imageUrl: null,
    franchiseId: 1,
    storeNumbers: null,
    isGlobal: true,
    isPinned: true,
    publishedAtUtc: daysAgo(11),
    expiresAtUtc: daysFromNow(90),
    createdAtUtc: daysAgo(12),
    createdByUserId: 2,
    createdByUserName: 'Mike Williams',
    updatedAtUtc: null,
    updatedByUserId: null,
    updatedByUserName: null,
    viewCount: 567,
    likeCount: 45,
    commentCount: 12,
    tags: [6, 5, 14],
  },
  {
    id: 3,
    title: 'Employee of the Month - November 2024',
    content: '<p>Congratulations to <strong>Emma Thompson</strong> from Store 005 for being named Employee of the Month!</p>',
    summary: 'Celebrating Emma Thompson from Store 005 as our November Employee of the Month',
    imageUrl: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=800',
    franchiseId: 1,
    storeNumbers: null,
    isGlobal: true,
    isPinned: false,
    publishedAtUtc: daysAgo(28),
    expiresAtUtc: daysFromNow(60),
    createdAtUtc: daysAgo(29),
    createdByUserId: 1,
    createdByUserName: 'Sarah Johnson',
    updatedAtUtc: null,
    updatedByUserId: null,
    updatedByUserName: null,
    viewCount: 289,
    likeCount: 156,
    commentCount: 34,
    tags: [7],
  },
  {
    id: 4,
    title: 'Store 001 - Lottery & Paypoint Promotion Week',
    content: '<p>Store 001 will be running an intensive Lottery & Paypoint promotion week.</p>',
    summary: 'Lottery & Paypoint promotion week at Store 001 to drive transactions and basket spend',
    imageUrl: null,
    franchiseId: 1,
    storeNumbers: ['001'],
    isGlobal: false,
    isPinned: false,
    publishedAtUtc: daysAgo(7),
    expiresAtUtc: daysFromNow(21),
    createdAtUtc: daysAgo(8),
    createdByUserId: 3,
    createdByUserName: 'David Chen',
    updatedAtUtc: null,
    updatedByUserId: null,
    updatedByUserName: null,
    viewCount: 45,
    likeCount: 12,
    commentCount: 5,
    tags: [3, 11],
  },
  {
    id: 5,
    title: 'System Maintenance Notice',
    content: '<p>Scheduled system maintenance will occur on Sunday from 02:00 to 06:00 GMT.</p>',
    summary: 'System maintenance scheduled',
    imageUrl: null,
    franchiseId: 1,
    storeNumbers: null,
    isGlobal: true,
    isPinned: false,
    publishedAtUtc: null,
    expiresAtUtc: daysFromNow(3),
    createdAtUtc: daysAgo(5),
    createdByUserId: 4,
    createdByUserName: 'Tech Support',
    updatedAtUtc: null,
    updatedByUserId: null,
    updatedByUserName: null,
    viewCount: 0,
    likeCount: 0,
    commentCount: 0,
    tags: [8, 12],
  },
  {
    id: 6,
    title: 'Christmas Trading Hours & Peak Season Preparation',
    content: '<p>Confirmed trading hours for the Christmas period.</p>',
    summary: 'Christmas trading hours and essential preparation checklist for peak trading period',
    imageUrl: 'https://images.unsplash.com/photo-1512389142860-9c449e58a814?w=800',
    franchiseId: 1,
    storeNumbers: null,
    isGlobal: true,
    isPinned: false,
    publishedAtUtc: daysAgo(14),
    expiresAtUtc: daysFromNow(60),
    createdAtUtc: daysAgo(15),
    createdByUserId: 1,
    createdByUserName: 'Sarah Johnson',
    updatedAtUtc: null,
    updatedByUserId: null,
    updatedByUserName: null,
    viewCount: 423,
    likeCount: 67,
    commentCount: 8,
    tags: [9, 13],
  },
  {
    id: 7,
    title: 'New Summer Pasta Specials: Gamberoni & Primavera',
    content: '<p>Two new seasonal pasta dishes launching across all Vapiano UK restaurants.</p>',
    summary: 'Two new seasonal pasta specials launching — Gamberoni & Primavera',
    imageUrl: 'https://images.unsplash.com/photo-1611270629569-8b357cb88da9?w=800',
    franchiseId: 10,
    storeNumbers: null,
    isGlobal: true,
    isPinned: true,
    publishedAtUtc: daysAgo(3),
    expiresAtUtc: daysFromNow(90),
    createdAtUtc: daysAgo(5),
    createdByUserId: 10,
    createdByUserName: 'Marco Bianchi',
    updatedAtUtc: daysAgo(3),
    updatedByUserId: 10,
    updatedByUserName: 'Marco Bianchi',
    viewCount: 187,
    likeCount: 64,
    commentCount: 12,
    tags: [15, 16, 20],
  },
  {
    id: 8,
    title: 'Open Kitchen Hygiene & Herb Garden Maintenance Standards',
    content: '<p>Reinforcing our open kitchen and herb garden standards.</p>',
    summary: 'Updated hygiene standards for open kitchens and care procedures for herb gardens',
    imageUrl: 'https://plus.unsplash.com/premium_photo-1733317481338-2cffa5ac3413?w=800',
    franchiseId: 10,
    storeNumbers: null,
    isGlobal: true,
    isPinned: false,
    publishedAtUtc: daysAgo(10),
    expiresAtUtc: daysFromNow(180),
    createdAtUtc: daysAgo(12),
    createdByUserId: 11,
    createdByUserName: 'Giulia Romano',
    updatedAtUtc: null,
    updatedByUserId: null,
    updatedByUserName: null,
    viewCount: 234,
    likeCount: 41,
    commentCount: 7,
    tags: [17, 18, 5],
  },
  {
    id: 12,
    title: 'Limited Time: Coffee Milkshake Mix-In with Jimmy\'s Iced Coffee',
    content: '<p>Our latest limited-time milkshake mix-in is here — Iced Coffee made with Jimmy\'s.</p>',
    summary: 'New limited-time milkshake mix-in — Jimmy\'s Iced Coffee',
    imageUrl: 'https://images.unsplash.com/photo-1572490122747-3968b75cc699?w=800',
    franchiseId: 11,
    storeNumbers: null,
    isGlobal: true,
    isPinned: true,
    publishedAtUtc: daysAgo(4),
    expiresAtUtc: daysFromNow(60),
    createdAtUtc: daysAgo(6),
    createdByUserId: 20,
    createdByUserName: 'Ryan Mitchell',
    updatedAtUtc: daysAgo(4),
    updatedByUserId: 20,
    updatedByUserName: 'Ryan Mitchell',
    viewCount: 312,
    likeCount: 89,
    commentCount: 15,
    tags: [21, 23, 3],
  },
  {
    id: 13,
    title: 'Peanut Allergen Awareness: Updated Signage Requirements',
    content: '<p>Following the latest FSA guidance, we are updating our peanut allergen signage.</p>',
    summary: 'Updated FSA-compliant peanut allergen signage requirements',
    imageUrl: 'https://images.unsplash.com/photo-1639070863891-a20035caf05a?w=800',
    franchiseId: 11,
    storeNumbers: null,
    isGlobal: true,
    isPinned: true,
    publishedAtUtc: daysAgo(6),
    expiresAtUtc: daysFromNow(120),
    createdAtUtc: daysAgo(8),
    createdByUserId: 21,
    createdByUserName: 'Sophie Turner',
    updatedAtUtc: null,
    updatedByUserId: null,
    updatedByUserName: null,
    viewCount: 445,
    likeCount: 32,
    commentCount: 8,
    tags: [24, 5, 14],
  },
];

const mockComments: NewsCommentDto[] = [
  { id: 1, newsItemId: 1, content: 'Excited for the new smoothies!', createdAtUtc: daysAgo(8), createdByUserId: 5, createdByUserName: 'Alex Brown', parentCommentId: null, likeCount: 12, isEdited: false },
  { id: 2, newsItemId: 1, content: 'When will the training materials be available?', createdAtUtc: daysAgo(8), createdByUserId: 6, createdByUserName: 'Jamie Wilson', parentCommentId: null, likeCount: 5, isEdited: false },
  { id: 3, newsItemId: 1, content: 'They are already in the Documents section under Training', createdAtUtc: daysAgo(8), createdByUserId: 1, createdByUserName: 'Sarah Johnson', parentCommentId: 2, likeCount: 8, isEdited: false },
  { id: 4, newsItemId: 3, content: 'Well deserved! Emma is amazing!', createdAtUtc: daysAgo(27), createdByUserId: 7, createdByUserName: 'Chris Lee', parentCommentId: null, likeCount: 25, isEdited: false },
  { id: 5, newsItemId: 3, content: 'Congratulations Emma!', createdAtUtc: daysAgo(27), createdByUserId: 8, createdByUserName: 'Pat Morgan', parentCommentId: null, likeCount: 18, isEdited: false },
];

let mockTags: NewsTagDto[] = [
  { id: 1, name: 'announcement', description: 'General announcements', color: '#3B82F6', isActive: true, usageCount: 5, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 2, name: 'menu', description: 'Menu updates and changes', color: '#10B981', isActive: true, usageCount: 8, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 3, name: 'promotion', description: 'Promotional events and offers', color: '#F59E0B', isActive: true, usageCount: 12, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 4, name: 'training', description: 'Training materials and updates', color: '#8B5CF6', isActive: true, usageCount: 3, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 5, name: 'compliance', description: 'Compliance and regulatory updates', color: '#EF4444', isActive: true, usageCount: 2, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 6, name: 'health-safety', description: 'Health and safety information', color: '#EC4899', isActive: true, usageCount: 7, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 7, name: 'recognition', description: 'Employee recognition and awards', color: '#14B8A6', isActive: true, usageCount: 4, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 8, name: 'maintenance', description: 'System maintenance notices', color: '#6B7280', isActive: true, usageCount: 2, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 9, name: 'holiday', description: 'Holiday schedules and information', color: '#F97316', isActive: true, usageCount: 6, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 10, name: 'policy', description: 'Policy updates and changes', color: '#64748B', isActive: true, usageCount: 1, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 11, name: 'event', description: 'Special events', color: '#A855F7', isActive: true, usageCount: 3, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 12, name: 'update', description: 'General updates', color: '#06B6D4', isActive: true, usageCount: 9, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 13, name: 'reminder', description: 'Reminders and deadlines', color: '#FBBF24', isActive: true, usageCount: 4, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 14, name: 'important', description: 'High priority items', color: '#DC2626', isActive: true, usageCount: 2, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 15, name: 'fresh-pasta', description: 'Fresh pasta and pasta station updates', color: '#D4213D', isActive: true, usageCount: 3, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 16, name: 'italian-cuisine', description: 'Italian food and recipe updates', color: '#5C7A29', isActive: true, usageCount: 2, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 17, name: 'open-kitchen', description: 'Open kitchen standards and operations', color: '#B5192C', isActive: true, usageCount: 1, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 18, name: 'herb-garden', description: 'Herb garden and olive tree care', color: '#6E8F35', isActive: true, usageCount: 1, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 19, name: 'bar-drinks', description: 'Bar, cocktails, and drinks menu', color: '#E8856A', isActive: true, usageCount: 1, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 20, name: 'pizza-oven', description: 'Pizza oven and pizza station', color: '#C91D34', isActive: true, usageCount: 1, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 21, name: 'burgers-grill', description: 'Burger and grill station updates', color: '#C92027', isActive: true, usageCount: 4, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 22, name: 'fries', description: 'Fries, fry station, and potato updates', color: '#F59E0B', isActive: true, usageCount: 2, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 23, name: 'milkshakes', description: 'Milkshake and mix-in updates', color: '#EC4899', isActive: true, usageCount: 1, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 24, name: 'allergen-safety', description: 'Allergen management and peanut safety', color: '#7C3AED', isActive: true, usageCount: 2, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 25, name: 'fresh-toppings', description: 'Toppings prep and freshness standards', color: '#059669', isActive: true, usageCount: 1, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
  { id: 26, name: 'quality-control', description: 'Quality standards and secret shopper', color: '#0284C7', isActive: true, usageCount: 1, createdAtUtc: '2024-01-01T00:00:00Z', updatedAtUtc: null },
];

let nextNewsItemId = 17;
let nextCommentId = 6;
let nextTagId = 27;

export class NewsClientMocked implements INewsClient {
  async getNewsItems(filters?: {
    status?: NewsItemStatus;
    franchiseId?: number;
    storeNumber?: string;
    searchText?: string;
    isPinned?: boolean;
    includeExpired?: boolean;
  }): Promise<NewsItemDto[]> {
    let items = [...mockNewsItems];

    if (filters?.status) {
      items = items.filter((i) => getNewsItemStatus(i.publishedAtUtc, i.expiresAtUtc) === filters.status);
    }
    // Demo data ignores franchise/store scoping entirely - return items regardless of
    // the requesting franchiseId/storeNumber.
    if (filters?.searchText) {
      const text = filters.searchText.toLowerCase();
      items = items.filter((i) =>
        i.title.toLowerCase().includes(text) ||
        i.summary.toLowerCase().includes(text) ||
        (i.tags && i.tags.some((tagId) => {
          const tag = mockTags.find((t) => t.id === tagId);
          return tag && tag.name.toLowerCase().includes(text);
        })),
      );
    }
    if (filters?.isPinned !== undefined) {
      items = items.filter((i) => i.isPinned === filters.isPinned);
    }
    if (!filters?.includeExpired) {
      const now = new Date().toISOString();
      items = items.filter((i) => !i.expiresAtUtc || i.expiresAtUtc > now);
    }

    items.sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      const dateA = a.publishedAtUtc || a.createdAtUtc;
      const dateB = b.publishedAtUtc || b.createdAtUtc;
      return dateB.localeCompare(dateA);
    });

    return items;
  }

  async getNewsItem(id: number): Promise<NewsItemDto | null> {
    return mockNewsItems.find((i) => i.id === id) ?? null;
  }

  async createNewsItem(dto: CreateUpdateNewsItemDto): Promise<NewsItemDto> {
    const newItem: NewsItemDto = {
      id: nextNewsItemId++,
      ...dto,
      createdAtUtc: new Date().toISOString(),
      createdByUserId: 1,
      createdByUserName: 'Current User',
      updatedAtUtc: null,
      updatedByUserId: null,
      updatedByUserName: null,
      viewCount: 0,
      likeCount: 0,
      commentCount: 0,
    };
    mockNewsItems.push(newItem);
    return newItem;
  }

  async updateNewsItem(id: number, dto: CreateUpdateNewsItemDto): Promise<NewsItemDto> {
    const index = mockNewsItems.findIndex((i) => i.id === id);
    if (index === -1) throw new Error(`News item ${id} not found`);

    const updated: NewsItemDto = {
      ...mockNewsItems[index],
      ...dto,
      updatedAtUtc: new Date().toISOString(),
    };
    mockNewsItems[index] = updated;
    return updated;
  }

  async deleteNewsItem(id: number): Promise<void> {
    mockNewsItems = mockNewsItems.filter((i) => i.id !== id);
  }

  async getComments(newsItemId: number): Promise<NewsCommentDto[]> {
    return mockComments.filter((c) => c.newsItemId === newsItemId);
  }

  async addComment(newsItemId: number, content: string): Promise<NewsCommentDto> {
    const newComment: NewsCommentDto = {
      id: nextCommentId++,
      newsItemId,
      content,
      createdAtUtc: new Date().toISOString(),
      createdByUserId: 1,
      createdByUserName: 'Current User',
      parentCommentId: null,
      likeCount: 0,
      isEdited: false,
    };
    mockComments.push(newComment);

    const itemIndex = mockNewsItems.findIndex((i) => i.id === newsItemId);
    if (itemIndex !== -1) mockNewsItems[itemIndex].commentCount++;

    return newComment;
  }

  async deleteComment(commentId: number): Promise<void> {
    const index = mockComments.findIndex((c) => c.id === commentId);
    if (index !== -1) {
      const newsItemId = mockComments[index].newsItemId;
      mockComments.splice(index, 1);
      const itemIndex = mockNewsItems.findIndex((i) => i.id === newsItemId);
      if (itemIndex !== -1) mockNewsItems[itemIndex].commentCount--;
    }
  }

  async getEngagementSummary(newsItemId: number): Promise<NewsEngagementSummaryDto> {
    const item = mockNewsItems.find((i) => i.id === newsItemId);
    if (!item) throw new Error(`News item ${newsItemId} not found`);

    return {
      newsItemId: item.id,
      viewCount: item.viewCount,
      likeCount: item.likeCount,
      commentCount: item.commentCount,
      uniqueViewers: Math.floor(item.viewCount * 0.85),
      engagementRate: item.viewCount > 0 ? ((item.likeCount + item.commentCount) / item.viewCount) * 100 : 0,
    };
  }

  async recordView(newsItemId: number): Promise<void> {
    const index = mockNewsItems.findIndex((i) => i.id === newsItemId);
    if (index !== -1) mockNewsItems[index].viewCount++;
  }

  async toggleLike(newsItemId: number, liked: boolean): Promise<NewsItemDto> {
    const index = mockNewsItems.findIndex((i) => i.id === newsItemId);
    if (index === -1) throw new Error(`News item ${newsItemId} not found`);

    if (liked) {
      mockNewsItems[index].likeCount += 1;
    } else {
      mockNewsItems[index].likeCount = Math.max(0, mockNewsItems[index].likeCount - 1);
    }
    return mockNewsItems[index];
  }

  async getTags(filters?: { activeOnly?: boolean; searchText?: string }): Promise<NewsTagDto[]> {
    let tags = [...mockTags];
    if (filters?.activeOnly) {
      tags = tags.filter((t) => t.isActive);
    }
    if (filters?.searchText) {
      const text = filters.searchText.toLowerCase();
      tags = tags.filter((t) => t.name.toLowerCase().includes(text) || (t.description && t.description.toLowerCase().includes(text)));
    }
    tags.sort((a, b) => a.name.localeCompare(b.name));
    return tags;
  }

  async getTag(id: number): Promise<NewsTagDto | null> {
    return mockTags.find((t) => t.id === id) ?? null;
  }

  async createTag(dto: CreateUpdateNewsTagDto): Promise<NewsTagDto> {
    const existing = mockTags.find((t) => t.name.toLowerCase() === dto.name.toLowerCase());
    if (existing) throw new Error(`Tag with name "${dto.name}" already exists`);

    const newTag: NewsTagDto = {
      id: nextTagId++,
      name: dto.name.toLowerCase().trim(),
      description: dto.description || null,
      color: dto.color || null,
      isActive: dto.isActive,
      usageCount: 0,
      createdAtUtc: new Date().toISOString(),
      updatedAtUtc: null,
    };
    mockTags.push(newTag);
    return newTag;
  }

  async updateTag(id: number, dto: CreateUpdateNewsTagDto): Promise<NewsTagDto> {
    const index = mockTags.findIndex((t) => t.id === id);
    if (index === -1) throw new Error(`Tag ${id} not found`);

    const existing = mockTags.find((t) => t.name.toLowerCase() === dto.name.toLowerCase() && t.id !== id);
    if (existing) throw new Error(`Tag with name "${dto.name}" already exists`);

    const updated: NewsTagDto = {
      ...mockTags[index],
      name: dto.name.toLowerCase().trim(),
      description: dto.description || null,
      color: dto.color || null,
      isActive: dto.isActive,
      updatedAtUtc: new Date().toISOString(),
    };
    mockTags[index] = updated;
    return updated;
  }

  async deleteTag(id: number): Promise<void> {
    mockTags = mockTags.filter((t) => t.id !== id);
  }
}
